import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Briefcase, Target, TrendingUp, Star, CheckCircle2, Award } from 'lucide-react';

const JobSeekers = () => {
  const benefits = [
    {
      icon: Target,
      title: 'Stand Out to Recruiters',
      description: 'Optimize your profile to appear in recruiter searches and attract top job opportunities.'
    },
    {
      icon: Award,
      title: 'Professional Branding',
      description: 'Build a compelling personal brand that showcases your unique value and expertise.'
    },
    {
      icon: TrendingUp,
      title: 'Increase Profile Views',
      description: 'Strategic optimization that increases your visibility by 300% on average.'
    }
  ];

  const features = [
    'Professional headline optimization',
    'Compelling "About" section that tells your story',
    'Experience section enhancement with achievement focus',
    'Skills optimization for recruiter searches',
    'Custom LinkedIn banner design',
    'Recommendation strategy and templates',
    'Job search strategy consultation',
    'Interview preparation tips'
  ];

  const packages = [
    {
      name: 'Profile Boost',
      price: '$199',
      description: 'Perfect for recent graduates and early career professionals',
      features: ['Profile audit', 'Headline & summary rewrite', 'Skills optimization', 'Email support']
    },
    {
      name: 'Career Accelerator',
      price: '$399',
      popular: true,
      description: 'Ideal for mid-level professionals seeking career advancement',
      features: ['Everything in Profile Boost', 'Custom banner design', 'Experience section enhancement', '30-day strategy support', 'Job search guidance']
    },
    {
      name: 'Executive Package',
      price: '$799',
      description: 'For senior professionals and executives',
      features: ['Everything in Career Accelerator', 'Personal branding strategy', 'Thought leadership content', '90-day support', '1-on-1 coaching sessions']
    }
  ];

  return (
    <div className="pt-16">
      <section className="py-32 bg-primary-dark text-text-inverse" data-testid="jobseekers-hero">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="inline-block text-secondary-main font-body uppercase tracking-widest text-xs mb-4" data-testid="hero-subtitle">
                For Job Seekers
              </span>
              <h1 className="font-heading text-4xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
                Land Your <span className="italic text-secondary-main">Dream Job</span> with a Powerful LinkedIn Profile
              </h1>
              <p className="text-text-inverse/80 text-lg leading-relaxed mb-8" data-testid="hero-description">
                Transform your LinkedIn profile into a magnet for recruiters and hiring managers. Our proven strategies help job seekers get noticed and land interviews.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/enquiry"
                  data-testid="hero-cta-primary"
                  className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center transition-all"
                >
                  Get Started Today
                </Link>
                <a
                  href="#packages"
                  data-testid="hero-cta-secondary"
                  className="border border-text-inverse text-text-inverse hover:bg-white/10 rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-flex items-center justify-center transition-all"
                >
                  View Packages
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20" data-testid="stat-1">
                <Briefcase className="w-10 h-10 text-secondary-main mb-3" />
                <h3 className="font-heading text-3xl font-bold mb-1">89%</h3>
                <p className="text-sm text-text-inverse/80">Success Rate</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20 mt-8" data-testid="stat-2">
                <Star className="w-10 h-10 text-secondary-main mb-3" />
                <h3 className="font-heading text-3xl font-bold mb-1">3000+</h3>
                <p className="text-sm text-text-inverse/80">Profiles Optimized</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background-main" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-primary-main mb-4" data-testid="benefits-title">
              Why LinkedIn Matters for Job Seekers
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">
              87% of recruiters use LinkedIn to find candidates. Make sure they find you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-primary-main/10 p-8 text-center hover:shadow-xl transition-all"
                  data-testid={`benefit-card-${index}`}
                >
                  <Icon className="w-12 h-12 text-secondary-main mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-bold text-primary-main mb-3">{benefit.title}</h3>
                  <p className="text-text-secondary leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl font-bold text-primary-main mb-6" data-testid="features-title">
                What's Included in Our Job Seeker Services
              </h2>
              <p className="text-text-secondary leading-relaxed mb-8">
                Comprehensive LinkedIn optimization designed specifically for job seekers. We help you present your best professional self to recruiters and hiring managers.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2" data-testid={`feature-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-secondary-main flex-shrink-0 mt-0.5" />
                    <span className="text-text-secondary text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-main to-primary-light p-8 text-text-inverse">
              <h3 className="font-heading text-2xl font-bold mb-4">Success Story</h3>
              <p className="text-text-inverse/90 mb-4 italic">
                "After GrowthPop optimized my LinkedIn profile, I received 5 interview requests in just 2 weeks. I landed my dream job at a Fortune 500 company!"
              </p>
              <div className="border-t border-white/20 pt-4">
                <p className="font-semibold">Sarah Johnson</p>
                <p className="text-sm text-text-inverse/80">Senior Marketing Manager</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="packages" className="py-20 bg-background-main" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-primary-main mb-4">
              Choose Your Career Package
            </h2>
            <p className="text-text-secondary text-lg">
              Invest in your career with packages designed for every stage of your professional journey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white border p-8 hover:shadow-xl transition-all relative ${
                  pkg.popular ? 'border-secondary-main border-2 shadow-lg' : 'border-primary-main/10'
                }`}
                data-testid={`package-${index}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-secondary-main text-primary-dark px-4 py-1 text-xs uppercase tracking-widest font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="font-heading text-2xl font-bold text-primary-main mb-2">{pkg.name}</h3>
                <div className="mb-4">
                  <span className="font-heading text-4xl font-bold text-primary-main">{pkg.price}</span>
                  <span className="text-text-muted"> one-time</span>
                </div>
                <p className="text-text-secondary text-sm leading-relaxed mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-secondary-main flex-shrink-0 mt-0.5" />
                      <span className="text-text-secondary text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/enquiry"
                  className={`w-full py-4 uppercase tracking-widest text-xs font-semibold rounded-none transition-all inline-block text-center ${
                    pkg.popular
                      ? 'bg-primary-main text-white hover:bg-primary-light'
                      : 'border border-primary-main text-primary-main hover:bg-primary-main hover:text-white'
                  }`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary-dark text-text-inverse">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl lg:text-5xl font-bold mb-6">
            Ready to Accelerate Your Job Search?
          </h2>
          <p className="text-text-inverse/80 text-lg mb-8">
            Join thousands of professionals who landed their dream jobs with our help.
          </p>
          <Link
            to="/enquiry"
            className="bg-secondary-main text-primary-dark hover:bg-secondary-light rounded-none px-8 py-4 uppercase tracking-widest text-xs font-semibold inline-block transition-all"
          >
            Start Your Transformation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JobSeekers;